import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const blank = () => ({
  type: "meal",
  capital_city: "",
  capital_country: "",
  restaurant: "",
  image_url: "",
  image_b64: "",
  published: true,
  en: { title: "", tagline: "", story: "", ingredients: [], steps: [] },
  es: { title: "", tagline: "", story: "", ingredients: [], steps: [] },
});

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const { t } = useI18n();
  const [recipes, setRecipes] = useState([]);
  const [edit, setEdit] = useState(null); // null | recipe
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) nav("/");
  }, [user, loading, nav]);

  const load = () => axios.get(`${API}/admin/recipes`).then(r => setRecipes(r.data)).catch(()=>{});
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const startNew = () => setEdit(blank());

  const save = async () => {
    try {
      if (edit.id) {
        await axios.put(`${API}/admin/recipes/${edit.id}`, edit);
        toast.success("Recipe updated");
      } else {
        await axios.post(`${API}/admin/recipes`, edit);
        toast.success("Recipe created");
      }
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this recipe?")) return;
    await axios.delete(`${API}/admin/recipes/${id}`);
    toast.success("Deleted");
    load();
  };

  const aiGen = async () => {
    if (!edit.capital_city || !edit.capital_country) {
      toast.error("Fill capital city & country first"); return;
    }
    try {
      setAiBusy(true);
      const res = await axios.post(`${API}/admin/ai/recipe`, {
        type: edit.type,
        capital_city: edit.capital_city,
        capital_country: edit.capital_country,
        inspiration: edit.en?.title || "",
      });
      setEdit(prev => ({ ...prev, en: res.data.en, es: res.data.es }));
      toast.success("AI recipe drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setAiBusy(false); }
  };

  const aiImg = async () => {
    const prompt = edit.en?.title || `${edit.type} from ${edit.capital_city}`;
    try {
      setImgBusy(true);
      const res = await axios.post(`${API}/admin/ai/image`, { prompt });
      setEdit(prev => ({ ...prev, image_b64: res.data.image_b64 }));
      toast.success("Dish image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image failed");
    } finally { setImgBusy(false); }
  };

  const [zipBusy, setZipBusy] = useState(false);
  const downloadAllPhotos = async () => {
    try {
      setZipBusy(true);
      const res = await axios.get(`${API}/admin/recipes/images.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "jacinto-atelier-photos.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("All dish photos downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setZipBusy(false); }
  };

  if (!user?.is_admin) return null;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline">Admin</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-2">{t("admin_title")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadAllPhotos}
            disabled={zipBusy}
            className="btn-ghost text-[11px] !py-2 !px-4"
            data-testid="admin-download-photos"
            title="Download every dish photo in a single ZIP file"
          >
            {zipBusy ? "Preparing…" : "↓ Download all photos"}
          </button>
          <button onClick={startNew} className="btn-gold" data-testid="admin-new-recipe">+ {t("admin_add")}</button>
        </div>
      </div>
      <div className="divider-gold my-8" />

      {edit ? (
        <Editor edit={edit} setEdit={setEdit} save={save} cancel={() => setEdit(null)} aiGen={aiGen} aiImg={aiImg} aiBusy={aiBusy} imgBusy={imgBusy} t={t} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recipes.map(r => (
            <div key={r.id} className="card-recipe p-4 flex gap-4" data-testid={`admin-recipe-${r.id}`}>
              <img src={r.image_b64 || r.image_url} alt="" className="w-24 h-24 object-cover" />
              <div className="flex-1">
                <div className="overline">{r.type} · £{r.price}</div>
                <div className="font-serif text-lg mt-1">{r.en.title}</div>
                <div className="text-xs text-zinc-500">{r.capital_city}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEdit(r)} className="tag tag-gold" data-testid={`admin-edit-${r.id}`}>Edit</button>
                  <button onClick={() => del(r.id)} className="tag" data-testid={`admin-delete-${r.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

function Editor({ edit, setEdit, save, cancel, aiGen, aiImg, aiBusy, imgBusy, t }) {
  const setField = (k, v) => setEdit(p => ({ ...p, [k]: v }));
  const setTr = (lang, k, v) => setEdit(p => ({ ...p, [lang]: { ...p[lang], [k]: v } }));
  const setList = (lang, k, idx, v) => setEdit(p => {
    const arr = [...(p[lang][k] || [])];
    arr[idx] = v;
    return { ...p, [lang]: { ...p[lang], [k]: arr } };
  });
  const addItem = (lang, k) => setEdit(p => ({ ...p, [lang]: { ...p[lang], [k]: [...(p[lang][k]||[]), ""] } }));
  const rmItem = (lang, k, idx) => setEdit(p => {
    const arr = (p[lang][k]||[]).filter((_,i)=>i!==idx);
    return { ...p, [lang]: { ...p[lang], [k]: arr } };
  });

  return (
    <div className="glass p-8" data-testid="admin-editor">
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <select className="fld" value={edit.type} onChange={e=>setField("type", e.target.value)} data-testid="admin-type">
          <option value="meal">Meal · £70</option>
          <option value="dessert">Dessert · £50</option>
        </select>
        <input className="fld" placeholder="Capital city" value={edit.capital_city} onChange={e=>setField("capital_city", e.target.value)} data-testid="admin-city" />
        <input className="fld" placeholder="Country" value={edit.capital_country} onChange={e=>setField("capital_country", e.target.value)} data-testid="admin-country" />
        <input className="fld" placeholder="Restaurant" value={edit.restaurant||""} onChange={e=>setField("restaurant", e.target.value)} data-testid="admin-restaurant" />
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={aiGen} disabled={aiBusy} className="btn-ghost" data-testid="admin-ai-generate">
          {aiBusy ? "Generating…" : `🪄 ${t("admin_ai_gen")}`}
        </button>
        <button onClick={aiImg} disabled={imgBusy} className="btn-ghost" data-testid="admin-ai-image">
          {imgBusy ? "Generating…" : `📸 ${t("admin_ai_img")}`}
        </button>
        <input className="fld flex-1 min-w-[300px]" placeholder="Image URL (or use AI)" value={edit.image_url||""} onChange={e=>setField("image_url", e.target.value)} data-testid="admin-image-url" />
      </div>

      {(edit.image_b64 || edit.image_url) && (
        <img src={edit.image_b64 || edit.image_url} alt="" className="w-60 h-40 object-cover mb-6 border border-white/10" data-testid="admin-image-preview" />
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {["en","es"].map(lang => (
          <div key={lang}>
            <div className="overline mb-3">{lang.toUpperCase()}</div>
            <input className="fld mb-3" placeholder="Title" value={edit[lang].title} onChange={e=>setTr(lang,"title",e.target.value)} data-testid={`admin-title-${lang}`} />
            <input className="fld mb-3" placeholder="Tagline" value={edit[lang].tagline||""} onChange={e=>setTr(lang,"tagline",e.target.value)} />
            <textarea className="fld mb-3 h-24" placeholder="Story" value={edit[lang].story||""} onChange={e=>setTr(lang,"story",e.target.value)} />
            <div className="overline mt-4 mb-2">Ingredients</div>
            {(edit[lang].ingredients||[]).map((it,idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input className="fld" value={it} onChange={e=>setList(lang,"ingredients",idx,e.target.value)} />
                <button onClick={()=>rmItem(lang,"ingredients",idx)} className="tag">×</button>
              </div>
            ))}
            <button onClick={()=>addItem(lang,"ingredients")} className="tag mb-4">+ ingredient</button>
            <div className="overline mt-4 mb-2">Steps</div>
            {(edit[lang].steps||[]).map((it,idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <textarea className="fld h-16" value={it} onChange={e=>setList(lang,"steps",idx,e.target.value)} />
                <button onClick={()=>rmItem(lang,"steps",idx)} className="tag">×</button>
              </div>
            ))}
            <button onClick={()=>addItem(lang,"steps")} className="tag">+ step</button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-8">
        <button onClick={save} className="btn-gold" data-testid="admin-save">{t("admin_save")}</button>
        <button onClick={cancel} className="btn-ghost" data-testid="admin-cancel">Cancel</button>
      </div>
    </div>
  );
}
